import { useEffect, useRef, useState } from 'react';

/**
 * useDeviceHeading — robust compass heading hook
 *
 * Returns:
 *   {
 *     heading:        true-north heading in degrees — UNWRAPPED (may drift
 *                     outside [0, 360) — mathematically identical for CSS
 *                     transforms and modular arithmetic, but visually
 *                     rotates by the shortest arc between updates),
 *     source:         'absolute' | 'webkit' | 'relative' | null,
 *     needPermission: true when iOS-style requestPermission() is required,
 *     unsupported:    true when no orientation API is available at all,
 *     requestPermission, // call to trigger the iOS prompt
 *   }
 *
 * Two smoothing techniques applied to eliminate the visible "spin jump"
 * that appeared just before the needle settled (see WhatsApp video from
 * a tester, Aug 13):
 *
 *   1. SHORTEST-ARC UNWRAPPING
 *      Raw device orientation is in [0, 360) — a step from 359° to 1° looks
 *      like a −358° swing to CSS `transform: rotate()`. We keep a running
 *      cumulative angle and add only the shortest signed delta
 *      (delta ∈ (−180°, 180°]) on every sample. CSS then always rotates by
 *      the short way.
 *
 *   2. EXPONENTIAL SMOOTHING (α = 0.18)
 *      Magnetic-sensor readings can spike by tens of degrees for a single
 *      frame. A light EMA absorbs those spikes without adding perceptible
 *      lag to real device motion.
 */
const screenAngle = () => {
  if (typeof window === 'undefined') return 0;
  return window.screen?.orientation?.angle
      ?? window.orientation
      ?? 0;
};

// Shortest-arc signed delta between two headings, in (−180, 180].
const shortestArc = (from, to) => {
  return ((to - from + 540) % 360) - 180;
};

const EMA_ALPHA = 0.18;

export function useDeviceHeading(declinationDeg = 0) {
  const [heading,        setHeading]        = useState(null);
  const [source,         setSource]         = useState(null);
  const [accuracy,       setAccuracy]       = useState(null); // iOS webkitCompassAccuracy (deg)
  const [needPermission, setNeedPermission] = useState(() => {
    if (typeof window === 'undefined' || !window.DeviceOrientationEvent) return false;
    return typeof window.DeviceOrientationEvent.requestPermission === 'function';
  });
  const [unsupported, setUnsupported] = useState(() => {
    if (typeof window === 'undefined') return false;
    return !window.DeviceOrientationEvent;
  });

  // Running smoothed & unwrapped heading — persists between events, never
  // reset by React state batching.
  const smoothedRef = useRef(null);

  /**
   * Feed a raw magnetic-North heading (0..360) into the smoothing pipeline
   * and publish the resulting cumulative, jitter-free heading.
   */
  const publishSmoothed = (rawMag) => {
    const trueRaw = (rawMag + declinationDeg - screenAngle() + 720) % 360;
    if (smoothedRef.current == null) {
      smoothedRef.current = trueRaw;
    } else {
      const delta = shortestArc(smoothedRef.current, trueRaw);
      // EMA on the delta gives noise rejection AND shortest-arc unwrap
      // in one step.
      smoothedRef.current = smoothedRef.current + EMA_ALPHA * delta;
    }
    setHeading(smoothedRef.current);
  };

  /* attach the listeners after permission is settled. */
  useEffect(() => {
    if (needPermission || unsupported) return undefined;
    if (typeof window === 'undefined' || !window.DeviceOrientationEvent) return undefined;

    let absoluteReceived = false;

    const absHandler = (e) => {
      if (typeof e.alpha !== 'number' || Number.isNaN(e.alpha)) return;
      absoluteReceived = true;
      // alpha 0 = magnetic North (Android Chrome SensorAPI).
      const magnetic = (360 - e.alpha + 360) % 360;
      publishSmoothed(magnetic);
      setSource('absolute');
    };

    const relHandler = (e) => {
      // Only use this branch if no absolute event ever arrived. We give the
      // absolute listener ~1 s head-start; after that we switch sources.
      if (absoluteReceived) return;
      let mag = null;
      let src = null;
      if (typeof e.webkitCompassHeading === 'number'
          && !Number.isNaN(e.webkitCompassHeading)) {
        mag = e.webkitCompassHeading;
        src = 'webkit';
        if (typeof e.webkitCompassAccuracy === 'number') setAccuracy(e.webkitCompassAccuracy);
      } else if (typeof e.alpha === 'number' && !Number.isNaN(e.alpha)) {
        mag = (360 - e.alpha + 360) % 360;
        src = 'relative';
      } else {
        return;
      }
      publishSmoothed(mag);
      setSource(src);
    };

    window.addEventListener('deviceorientationabsolute', absHandler, true);
    window.addEventListener('deviceorientation',          relHandler, true);

    return () => {
      window.removeEventListener('deviceorientationabsolute', absHandler, true);
      window.removeEventListener('deviceorientation',          relHandler, true);
    };
  }, [needPermission, unsupported, declinationDeg]);

  /* on screen-orientation change we don't need to nudge state — the very
     next sensor tick will re-anchor smoothly through the same pipeline. */

  const requestPermission = async () => {
    if (typeof window === 'undefined' || !window.DeviceOrientationEvent) return;
    if (typeof window.DeviceOrientationEvent.requestPermission !== 'function') {
      setNeedPermission(false);
      return;
    }
    try {
      const res = await window.DeviceOrientationEvent.requestPermission();
      if (res === 'granted') setNeedPermission(false);
      else setUnsupported(true);
    } catch {
      setUnsupported(true);
    }
  };

  return { heading, source, accuracy, needPermission, unsupported, requestPermission };
}
